import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Link2, RefreshCw, Loader2, CheckCircle2, AlertCircle, Pencil, Trash2,
  ShieldCheck, ShieldAlert, Upload, History as HistoryIcon, PlugZap,
  Settings2, Power, PowerOff, Eye, Plus, Database, Sparkles, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------- helpers ----------
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_e) { return iso; }
};

const Stat = ({ label, value, tone = 'default', testId, hint }) => {
  const tones = {
    default: 'bg-muted',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    danger:  'bg-rose-50 text-rose-700 border-rose-200',
    warn:    'bg-amber-50 text-amber-700 border-amber-200',
    info:    'bg-sky-50 text-sky-700 border-sky-200',
  };
  return (
    <div className={`border rounded-lg p-4 ${tones[tone]}`} data-testid={testId}>
      <p className="text-xs uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-3xl font-semibold mt-1">{value ?? '—'}</p>
      {hint && <p className="text-xs opacity-70 mt-1">{hint}</p>}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  if (status === 'active') {
    return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>;
  }
  return <Badge variant="secondary" className="bg-zinc-100 text-zinc-600">Disabled</Badge>;
};

const SOURCE_TONE = {
  LMS: 'destructive',
  Servall: 'secondary',
  R2G: 'secondary',
  lead_database: 'outline',
  Batch: 'outline',
};

// ---------- main page ----------
const SheetMonitorPage = () => {
  const { api } = useAuth();

  // Sheet list + selected sheet
  const [loading, setLoading] = useState(true);
  const [sheets, setSheets] = useState([]);
  const [activeView, setActiveView] = useState('list');     // 'list' | 'detail' | 'mapping'
  const [activeSheetId, setActiveSheetId] = useState(null);

  // Dialogs
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addUrl, setAddUrl] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [addSuggested, setAddSuggested] = useState(null);  // headers + suggestion to bring user into mapping screen

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Set Anchor Manually dialog
  const [anchorDialogOpen, setAnchorDialogOpen] = useState(false);
  const [anchorTarget, setAnchorTarget] = useState(null);   // sheet
  const [anchorMobile, setAnchorMobile] = useState('');
  const [anchorName, setAnchorName] = useState('');
  const [anchorSaving, setAnchorSaving] = useState(false);

  // Mapping editor state
  const [mapLoading, setMapLoading] = useState(false);
  const [mapData, setMapData] = useState(null);             // {headers, sampleRows, lmsFields, suggestedMapping, savedMapping, mappingSaved}
  const [mapDraft, setMapDraft] = useState({});             // current draft
  const [mapSaving, setMapSaving] = useState(false);

  // Check workflow (per-sheet OR global)
  const [checking, setChecking] = useState(false);
  const [checkScope, setCheckScope] = useState(null);       // {type: 'single' | 'all', sheetId?}
  const [checkResult, setCheckResult] = useState(null);     // {perSheet, newLeads} or single result
  const [checkingDup, setCheckingDup] = useState(false);
  const [dupResult, setDupResult] = useState(null);
  const [selected, setSelected] = useState({});             // key (sheetId|mobile) -> bool
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // History (per-sheet) — loaded when in detail view
  const [historyItems, setHistoryItems] = useState([]);

  // ---------- data loaders ----------
  const refreshSheets = useCallback(async () => {
    try {
      const { data } = await api.get('/google-sheets/');
      setSheets(data.items || []);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to load sheets';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load sheets');
    }
  }, [api]);

  const refreshSheetHistory = useCallback(async (sheetId) => {
    if (!sheetId) return;
    try {
      const { data } = await api.get(`/google-sheets/${sheetId}/history?limit=50`);
      setHistoryItems(data.items || []);
    } catch (_e) { /* silent */ }
  }, [api]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await refreshSheets();
      setLoading(false);
    })();
  }, [refreshSheets]);

  // ---------- helpers ----------
  const activeSheet = useMemo(
    () => sheets.find((s) => s.id === activeSheetId),
    [sheets, activeSheetId],
  );

  const resetWorkflow = () => {
    setCheckScope(null);
    setCheckResult(null);
    setDupResult(null);
    setSelected({});
    setImportResult(null);
  };

  const leadKey = (lead) => `${lead.sheetId}|${lead.mobile}`;

  // ---------- ADD SHEET ----------
  const handleAddSheet = async () => {
    if (!addName.trim() || !addUrl.trim()) {
      return toast.error('Both Sheet Name and URL are required');
    }
    setAddSaving(true);
    try {
      const { data } = await api.post('/google-sheets/', {
        name: addName.trim(),
        sheetUrl: addUrl.trim(),
      });
      toast.success(`Sheet "${data.sheet.name}" connected. Now save its column mapping.`);
      await refreshSheets();
      setAddDialogOpen(false);
      setAddName('');
      setAddUrl('');
      setAddSuggested(null);
      // Immediately open mapping editor
      setActiveSheetId(data.sheet.id);
      setActiveView('mapping');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to add sheet';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setAddSaving(false);
    }
  };

  // ---------- EDIT SHEET ----------
  const openEditDialog = (s) => {
    setEditTarget(s);
    setEditName(s.name);
    setEditUrl(s.sheetUrl);
    setEditDialogOpen(true);
  };

  const handleEditSheet = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await api.put(`/google-sheets/${editTarget.id}`, {
        name: editName.trim() || undefined,
        sheetUrl: editUrl.trim() || undefined,
      });
      toast.success('Sheet updated');
      await refreshSheets();
      setEditDialogOpen(false);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Update failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setEditSaving(false);
    }
  };

  // ---------- TOGGLE ENABLE/DISABLE ----------
  const handleToggle = async (s) => {
    try {
      const { data } = await api.post(`/google-sheets/${s.id}/toggle`);
      toast.success(`Sheet "${s.name}" is now ${data.status === 'active' ? 'enabled' : 'disabled'}`);
      await refreshSheets();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Toggle failed';
      toast.error(typeof msg === 'string' ? msg : 'Toggle failed');
    }
  };

  // ---------- SET ANCHOR MANUALLY ----------
  const openAnchorDialog = (s) => {
    setAnchorTarget(s);
    setAnchorMobile(s?.anchorMobile || '');
    setAnchorName(s?.anchorName || '');
    setAnchorDialogOpen(true);
  };

  const handleSaveAnchor = async () => {
    if (!anchorTarget) return;
    const mobileDigits = (anchorMobile || '').replace(/\D+/g, '');
    if (mobileDigits.length < 10) {
      toast.error('Please enter at least 10 digits for the mobile (last 10 will be used).');
      return;
    }
    setAnchorSaving(true);
    try {
      const { data } = await api.post(`/google-sheets/${anchorTarget.id}/set-anchor`, {
        mobile: mobileDigits,
        name: anchorName.trim() || undefined,
      });
      toast.success(
        `Anchor set: ${data.anchor.name || '(no name)'} · ${data.anchor.mobile}. ` +
        `Next "Check New Leads" will stop at this lead.`
      );
      await refreshSheets();
      setAnchorDialogOpen(false);
      setAnchorTarget(null);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to set anchor';
      toast.error(typeof msg === 'string' ? msg : 'Failed to set anchor');
    } finally {
      setAnchorSaving(false);
    }
  };

  const handleClearAnchor = async (s) => {
    if (!s) return;
    if (!window.confirm(
      `Clear the anchor for "${s.name}"?\n\n` +
      'After clearing, the next "Check New Leads" will return ALL valid rows in the sheet ' +
      '(cold start). The anchor will be re-set automatically on the next successful import.'
    )) return;
    try {
      await api.post(`/google-sheets/${s.id}/clear-anchor`);
      toast.success('Anchor cleared. Next check will return all rows.');
      await refreshSheets();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to clear anchor';
      toast.error(typeof msg === 'string' ? msg : 'Failed to clear anchor');
    }
  };

  // ---------- DELETE ----------
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/google-sheets/${deleteTarget.id}`);
      toast.success(`Sheet "${deleteTarget.name}" disconnected. Your LMS leads are untouched.`);
      await refreshSheets();
      setDeleteTarget(null);
      if (activeSheetId === deleteTarget.id) {
        setActiveSheetId(null);
        setActiveView('list');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Delete failed';
      toast.error(typeof msg === 'string' ? msg : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // ---------- OPEN SHEET ----------
  const openSheet = async (s) => {
    setActiveSheetId(s.id);
    setActiveView('detail');
    resetWorkflow();
    await refreshSheetHistory(s.id);
  };

  // ---------- MAPPING ----------
  const openMappingEditor = async (s) => {
    setActiveSheetId(s.id);
    setActiveView('mapping');
    setMapLoading(true);
    setMapData(null);
    try {
      const { data } = await api.get(`/google-sheets/${s.id}/columns`);
      setMapData(data);
      // Seed draft: prefer saved mapping; fall back to suggestion
      const draft = {};
      (data.lmsFields || []).forEach((f) => {
        const saved = data.savedMapping?.[f.key];
        const sug = data.suggestedMapping?.[f.key];
        draft[f.key] = (saved !== undefined && saved !== null && saved !== '') ? saved : (sug || '');
      });
      setMapDraft(draft);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to load columns';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load columns');
    } finally {
      setMapLoading(false);
    }
  };

  const applySuggestion = () => {
    if (!mapData) return;
    const draft = {};
    (mapData.lmsFields || []).forEach((f) => {
      draft[f.key] = mapData.suggestedMapping?.[f.key] || '';
    });
    setMapDraft(draft);
    toast.info('Smart suggestion applied — review and Save');
  };

  const saveMapping = async () => {
    if (!activeSheetId) return;
    const mapping = {};
    Object.keys(mapDraft).forEach((k) => {
      mapping[k] = mapDraft[k] && mapDraft[k] !== '__none__' ? mapDraft[k] : null;
    });
    if (!mapping.mobile) {
      return toast.error('Mobile is required — please map a sheet column to "Mobile" before saving');
    }
    setMapSaving(true);
    try {
      await api.put(`/google-sheets/${activeSheetId}/mapping`, { mapping });
      toast.success('Mapping saved. Monitoring is now active for this sheet.');
      await refreshSheets();
      setActiveView('detail');
      await refreshSheetHistory(activeSheetId);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setMapSaving(false);
    }
  };

  // ---------- CHECK NEW (single sheet) ----------
  const handleCheckNewSingle = async (s) => {
    if (!s.mappingSaved) {
      toast.error('Save the column mapping first');
      return openMappingEditor(s);
    }
    setChecking(true);
    setCheckScope({ type: 'single', sheetId: s.id, sheetName: s.name });
    setCheckResult(null);
    setDupResult(null);
    setSelected({});
    setImportResult(null);
    try {
      const { data } = await api.post(`/google-sheets/${s.id}/check-new`);
      setCheckResult({
        ...data,
        perSheet: [{
          sheetId: data.sheetId,
          sheetName: data.sheetName,
          status: data.status,
          newLeadsCount: data.newLeadsCount,
          totalRowsInSheet: data.totalRowsInSheet,
          scannedRows: data.scannedRows,
          stoppedAtRow: data.stoppedAtRow,
          anchor: data.anchor,
          diagnostics: data.diagnostics,
          error: data.error,
        }],
      });
      if (data.newLeadsCount === 0) {
        if (data.error) {
          toast.error(data.error);
        } else {
          toast.info(`No new leads in "${s.name}" (scanned ${data.scannedRows} of ${data.totalRowsInSheet} rows)`);
        }
      } else {
        toast.success(`${data.newLeadsCount} new lead${data.newLeadsCount === 1 ? '' : 's'} detected in "${s.name}"`);
      }
      await refreshSheets();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Check failed';
      toast.error(typeof msg === 'string' ? msg : 'Check failed');
    } finally {
      setChecking(false);
    }
  };

  // ---------- CHECK ALL NEW (global) ----------
  const handleCheckAll = async () => {
    setChecking(true);
    setCheckScope({ type: 'all' });
    setCheckResult(null);
    setDupResult(null);
    setSelected({});
    setImportResult(null);
    setActiveView('list');
    try {
      const { data } = await api.post('/google-sheets/check-all-new');
      setCheckResult(data);
      if (data.totalNewLeads === 0) {
        toast.info(`No new leads across ${data.checkedSheets} active sheet${data.checkedSheets === 1 ? '' : 's'}.`);
      } else {
        toast.success(`${data.totalNewLeads} new lead${data.totalNewLeads === 1 ? '' : 's'} from ${data.checkedSheets} sheet${data.checkedSheets === 1 ? '' : 's'}`);
      }
      await refreshSheets();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Check All failed';
      toast.error(typeof msg === 'string' ? msg : 'Check All failed');
    } finally {
      setChecking(false);
    }
  };

  // ---------- CHECK DUPLICATES ----------
  const handleCheckDuplicates = async () => {
    if (!checkResult?.newLeads?.length) return;
    setCheckingDup(true);
    setDupResult(null);
    setSelected({});
    try {
      const { data } = await api.post('/google-sheets/check-duplicates', {
        items: checkResult.newLeads,
      });
      setDupResult(data);
      const pre = {};
      (data.uniqueLeads || []).forEach((l) => { pre[leadKey(l)] = true; });
      setSelected(pre);
      toast.success(`${data.uniqueCount} unique · ${data.duplicateCount} duplicate`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Duplicate check failed';
      toast.error(typeof msg === 'string' ? msg : 'Duplicate check failed');
    } finally {
      setCheckingDup(false);
    }
  };

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected],
  );
  const toggleAll = () => {
    if (!dupResult?.uniqueLeads) return;
    const allOn = dupResult.uniqueLeads.every((l) => selected[leadKey(l)]);
    const next = {};
    dupResult.uniqueLeads.forEach((l) => { next[leadKey(l)] = !allOn; });
    setSelected(next);
  };

  // ---------- IMPORT ----------
  const handleImport = async () => {
    if (!dupResult?.uniqueLeads?.length) return;
    const chosen = dupResult.uniqueLeads.filter((l) => selected[leadKey(l)]);
    if (chosen.length === 0) return toast.error('Select at least one lead to import');
    setImporting(true);
    try {
      const { data } = await api.post('/google-sheets/import', { items: chosen });
      setImportResult(data);
      toast.success(`${data.imported} lead${data.imported === 1 ? '' : 's'} imported to LMS`);
      await refreshSheets();
      if (activeSheetId) await refreshSheetHistory(activeSheetId);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Import failed';
      toast.error(typeof msg === 'string' ? msg : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // ---------- LIST VIEW: search + pagination ----------
  const [searchQ, setSearchQ] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // Always reset to page 1 when the search query changes
  useEffect(() => { setPage(1); }, [searchQ]);

  const filteredSheets = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return sheets;
    return sheets.filter((s) =>
      (s.name || '').toLowerCase().includes(q)
      || (s.sheetUrl || '').toLowerCase().includes(q),
    );
  }, [sheets, searchQ]);

  const pagedSheets = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredSheets.slice(start, start + PAGE_SIZE);
  }, [filteredSheets, page]);

  const totalPages = Math.max(1, Math.ceil(filteredSheets.length / PAGE_SIZE));

  // ============================================================
  // RENDER
  // ============================================================
  if (loading) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="google-sheets-engine-page">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Database className="h-7 w-7 text-emerald-600" /> Google Sheets Engine
          </h1>
          <p className="text-muted-foreground mt-1">
            Connect Google Sheets to your CRM. Each sheet keeps its own column mapping, monitoring history and duplicate-checked imports.
          </p>
        </div>
        {activeView === 'list' && (
          <Button
            onClick={() => { setAddName(''); setAddUrl(''); setAddDialogOpen(true); }}
            size="lg"
            data-testid="add-sheet-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Sheet
          </Button>
        )}
        {activeView !== 'list' && (
          <Button
            variant="outline"
            onClick={() => { setActiveView('list'); setActiveSheetId(null); resetWorkflow(); }}
            data-testid="back-to-list-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Sheets
          </Button>
        )}
      </div>

      {/* ============================================================
          LIST VIEW
          ============================================================ */}
      {activeView === 'list' && (
        <>
          {sheets.length === 0 ? (
            <Card data-testid="empty-state-card" className="border-dashed">
              <CardContent className="py-14 text-center space-y-4">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center">
                  <PlugZap className="h-7 w-7 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">No sheets connected yet</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                    Add a Google Sheet to start importing leads into your CRM.
                    Each sheet keeps its own column mapping and monitoring history.
                  </p>
                </div>
                <Button onClick={() => { setAddName(''); setAddUrl(''); setAddDialogOpen(true); }}
                  size="lg" data-testid="add-first-sheet-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Your First Sheet
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card data-testid="sheet-list-card">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="text-lg">Connected Sheets</CardTitle>
                    <CardDescription>
                      {filteredSheets.length} of {sheets.length} sheet{sheets.length === 1 ? '' : 's'}
                      {searchQ.trim() ? ` matching "${searchQ.trim()}"` : ''}
                    </CardDescription>
                  </div>
                  <div className="relative w-full md:w-80">
                    <Input
                      placeholder="Search by name or URL…"
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      data-testid="sheet-search-input"
                      className="pl-9"
                    />
                    <RefreshCw className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground opacity-0" aria-hidden />
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="11" cy="11" r="7" />
                      <path d="M21 21l-4.3-4.3" />
                    </svg>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredSheets.length === 0 ? (
                  <div className="py-14 text-center text-sm text-muted-foreground">
                    No sheets match your search.
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/40">
                            <TableHead className="font-semibold">Sheet</TableHead>
                            <TableHead className="font-semibold">Status</TableHead>
                            <TableHead className="font-semibold whitespace-nowrap">Last Checked</TableHead>
                            <TableHead className="font-semibold whitespace-nowrap">Last Imported</TableHead>
                            <TableHead className="font-semibold text-right whitespace-nowrap">Total Imported</TableHead>
                            <TableHead className="font-semibold text-right whitespace-nowrap">Total Duplicate</TableHead>
                            <TableHead className="font-semibold text-right whitespace-nowrap">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pagedSheets.map((s) => (
                            <TableRow key={s.id} data-testid={`sheet-row-${s.id}`} className="hover:bg-muted/30">
                              <TableCell className="py-4">
                                <div className="font-semibold text-foreground">{s.name}</div>
                                <div className="flex items-center gap-2 mt-1">
                                  {s.mappingSaved
                                    ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Mapping ready</Badge>
                                    : <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Mapping required</Badge>}
                                  <a
                                    href={s.sheetUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs text-muted-foreground hover:text-sky-700 hover:underline truncate max-w-[260px] inline-flex items-center gap-1"
                                    title={s.sheetUrl}
                                  >
                                    <Link2 className="h-3 w-3" />
                                    {(s.sheetUrl || '').length > 38 ? (s.sheetUrl || '').slice(0, 38) + '…' : s.sheetUrl}
                                  </a>
                                </div>
                              </TableCell>
                              <TableCell><StatusBadge status={s.status} /></TableCell>
                              <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                {fmtDateTime(s.lastCheckedAt)}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                {fmtDateTime(s.lastImportedAt)}
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold text-emerald-700">
                                {s.totalImportedLeads ?? 0}
                              </TableCell>
                              <TableCell className="text-right font-mono font-semibold text-rose-600">
                                {s.totalDuplicateLeads ?? 0}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="inline-flex items-center gap-1">
                                  <Button size="sm" variant="outline"
                                    onClick={() => openSheet(s)}
                                    data-testid={`open-${s.id}`}>
                                    <Eye className="h-3.5 w-3.5 mr-1" /> Open
                                  </Button>
                                  <Button size="sm"
                                    onClick={() => handleCheckNewSingle(s)}
                                    disabled={checking || !s.mappingSaved || s.status !== 'active'}
                                    data-testid={`check-${s.id}`}>
                                    {checking && checkScope?.sheetId === s.id
                                      ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                      : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                                    Check New
                                  </Button>
                                  <Button size="sm" variant="outline"
                                    onClick={() => openMappingEditor(s)}
                                    data-testid={`map-${s.id}`}>
                                    <Settings2 className="h-3.5 w-3.5 mr-1" /> Edit Mapping
                                  </Button>
                                  <Button size="icon" variant="ghost"
                                    onClick={() => setDeleteTarget(s)}
                                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-9 w-9"
                                    title="Delete sheet"
                                    data-testid={`delete-${s.id}`}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
                        <div className="text-muted-foreground">
                          Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredSheets.length)} of {filteredSheets.length}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="page-prev">
                            Previous
                          </Button>
                          <span className="px-2 text-muted-foreground">Page {page} of {totalPages}</span>
                          <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="page-next">
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Global "Check All" results — review screen (rendered only after a per-sheet or all-sheets check) */}
          {checkResult && (
            <CheckResultsView
              checkResult={checkResult}
              checkScope={checkScope}
              dupResult={dupResult}
              checkingDup={checkingDup}
              handleCheckDuplicates={handleCheckDuplicates}
              selected={selected}
              setSelected={setSelected}
              selectedCount={selectedCount}
              toggleAll={toggleAll}
              leadKey={leadKey}
              importing={importing}
              handleImport={handleImport}
              importResult={importResult}
              resetWorkflow={resetWorkflow}
            />
          )}
        </>
      )}

      {/* ============================================================
          DETAIL VIEW (single sheet)
          ============================================================ */}
      {activeView === 'detail' && activeSheet && (
        <SheetDetailView
          sheet={activeSheet}
          checking={checking}
          checkScope={checkScope}
          handleCheckNewSingle={handleCheckNewSingle}
          openMappingEditor={openMappingEditor}
          checkResult={checkResult}
          dupResult={dupResult}
          checkingDup={checkingDup}
          handleCheckDuplicates={handleCheckDuplicates}
          selected={selected}
          setSelected={setSelected}
          selectedCount={selectedCount}
          toggleAll={toggleAll}
          leadKey={leadKey}
          importing={importing}
          handleImport={handleImport}
          importResult={importResult}
          resetWorkflow={resetWorkflow}
          historyItems={historyItems}
          handleToggle={handleToggle}
          openEditDialog={openEditDialog}
          openAnchorDialog={openAnchorDialog}
          handleClearAnchor={handleClearAnchor}
        />
      )}

      {/* ============================================================
          MAPPING VIEW
          ============================================================ */}
      {activeView === 'mapping' && (
        <MappingEditor
          sheet={activeSheet}
          loading={mapLoading}
          data={mapData}
          draft={mapDraft}
          setDraft={setMapDraft}
          applySuggestion={applySuggestion}
          saving={mapSaving}
          onSave={saveMapping}
          onCancel={() => setActiveView(activeSheet ? 'detail' : 'list')}
        />
      )}

      {/* ============================================================
          DIALOGS
          ============================================================ */}

      {/* Add sheet */}
      <Dialog open={addDialogOpen} onOpenChange={(o) => { setAddDialogOpen(o); if (!o) setAddSuggested(null); }}>
        <DialogContent data-testid="add-sheet-dialog">
          <DialogHeader>
            <DialogTitle>Add Google Sheet</DialogTitle>
            <DialogDescription>
              After connecting, you&apos;ll be taken to the column mapping screen to finalize setup.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="add-name">Sheet Name *</Label>
              <Input id="add-name" value={addName} onChange={(e) => setAddName(e.target.value)}
                placeholder="e.g. Meta Ads · Website Leads · IndiaMART"
                data-testid="add-name-input" />
            </div>
            <div>
              <Label htmlFor="add-url">Google Sheet URL *</Label>
              <Input id="add-url" value={addUrl} onChange={(e) => setAddUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/.../edit#gid=0"
                data-testid="add-url-input" />
            </div>
            <Alert>
              <ShieldAlert className="h-4 w-4" />
              <AlertTitle>Make sure sharing is &quot;Anyone with the link → Viewer&quot;</AlertTitle>
              <AlertDescription>
                We don&apos;t store your Google credentials. We fetch the sheet via its public CSV export URL.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} disabled={addSaving}>
              Cancel
            </Button>
            <Button onClick={handleAddSheet} disabled={addSaving} data-testid="add-save-btn">
              {addSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
              Connect & Map
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit sheet */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent data-testid="edit-sheet-dialog">
          <DialogHeader>
            <DialogTitle>Edit Sheet</DialogTitle>
            <DialogDescription>Update the sheet&apos;s name or URL.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="edit-name">Sheet Name</Label>
              <Input id="edit-name" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="edit-url">Google Sheet URL</Label>
              <Input id="edit-url" value={editUrl} onChange={(e) => setEditUrl(e.target.value)} />
            </div>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Changing the URL does NOT reset imported leads. If the new sheet has different
                column names, you&apos;ll need to update the mapping.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={editSaving}>
              Cancel
            </Button>
            <Button onClick={handleEditSheet} disabled={editSaving} data-testid="edit-save-btn">
              {editSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Pencil className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-sheet-dialog">
          <DialogHeader>
            <DialogTitle>Disconnect &quot;{deleteTarget?.name}&quot;?</DialogTitle>
            <DialogDescription>
              This removes the sheet configuration, its column mapping, and its monitoring history.
              <br /><br />
              <strong className="text-emerald-700">Your LMS leads, Servall registry, R2G registry,
              and already-imported leads remain completely untouched.</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}
              data-testid="delete-confirm-btn">
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Disconnect Sheet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================
          SET ANCHOR MANUALLY DIALOG
          ============================================================ */}
      <Dialog open={anchorDialogOpen} onOpenChange={(o) => {
        setAnchorDialogOpen(o);
        if (!o) { setAnchorTarget(null); setAnchorMobile(''); setAnchorName(''); }
      }}>
        <DialogContent data-testid="set-anchor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              {anchorTarget?.anchorMobile ? 'Override Anchor (Admin)' : 'Set Initial Anchor'}
              {anchorTarget?.name && <span className="text-sm text-muted-foreground font-normal">· {anchorTarget.name}</span>}
            </DialogTitle>
            <DialogDescription>
              {anchorTarget?.anchorMobile ? (
                <>
                  <span className="text-rose-700 font-medium">Admin / Recovery only.</span>
                  &nbsp;The anchor normally moves forward <strong>automatically</strong> after every successful
                  import — you only need this dialog to recover from a corrupted state, point at a different
                  baseline, or for debugging.
                </>
              ) : (
                <>
                  <strong>One-time setup.</strong> Tell the system which lead is your most-recent already-imported
                  one (i.e. the &quot;last successfully imported lead&quot;). From that moment on, the anchor
                  auto-advances on every &quot;Import Selected Leads&quot; — <strong>you will never have to touch
                  it again</strong>.
                </>
              )}
              <br /><br />
              On every &quot;Check New Leads&quot; the scan reads the sheet top → bottom and stops the moment it
              hits this mobile. Only rows <em>above</em> it are returned as new.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="anchor-mobile">Mobile <span className="text-rose-600">*</span></Label>
              <Input
                id="anchor-mobile"
                data-testid="anchor-mobile-input"
                value={anchorMobile}
                onChange={(e) => setAnchorMobile(e.target.value)}
                placeholder="e.g. 8852806191 (last 10 digits used for matching)"
                className="font-mono"
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">
                Country code / spaces / hyphens are auto-stripped. Only the last 10 digits matter.
              </p>
            </div>
            <div>
              <Label htmlFor="anchor-name">Name (optional)</Label>
              <Input
                id="anchor-name"
                data-testid="anchor-name-input"
                value={anchorName}
                onChange={(e) => setAnchorName(e.target.value)}
                placeholder="e.g. Surendra Pareek"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Display-only — used in logs and the anchor chip.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAnchorDialogOpen(false)} disabled={anchorSaving}>
              Cancel
            </Button>
            <Button onClick={handleSaveAnchor} disabled={anchorSaving || !anchorMobile.trim()}
              data-testid="save-anchor-btn">
              {anchorSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              {anchorTarget?.anchorMobile ? 'Update Anchor' : 'Set as Anchor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// SUB-COMPONENT: CHECK RESULTS REVIEW (used by both 'list' global view & 'detail' per-sheet view)
// ============================================================
const CheckResultsView = ({
  checkResult, checkScope,
  dupResult, checkingDup, handleCheckDuplicates,
  selected, setSelected, selectedCount, toggleAll, leadKey,
  importing, handleImport, importResult, resetWorkflow,
}) => {
  const newLeads = checkResult.newLeads || [];
  const perSheet = checkResult.perSheet || [];

  return (
    <div className="space-y-6">
      {/* Per-sheet summary */}
      <Card data-testid="check-summary-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle>
                {checkScope?.type === 'all'
                  ? `Check All — ${checkResult.checkedSheets ?? perSheet.length} active sheet${(checkResult.checkedSheets ?? perSheet.length) === 1 ? '' : 's'}`
                  : `New Leads from "${checkScope?.sheetName}"`}
              </CardTitle>
              <CardDescription>
                {checkScope?.type === 'all'
                  ? `${checkResult.totalNewLeads ?? newLeads.length} new lead${(checkResult.totalNewLeads ?? newLeads.length) === 1 ? '' : 's'} merged from all active sheets`
                  : `${newLeads.length} new lead${newLeads.length === 1 ? '' : 's'} detected since last import`}
              </CardDescription>
            </div>
            {newLeads.length > 0 && !dupResult && (
              <Button onClick={handleCheckDuplicates} disabled={checkingDup} size="lg"
                data-testid="check-duplicates-btn">
                {checkingDup
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <ShieldCheck className="h-4 w-4 mr-2" />}
                Check Duplicates
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* ============================================================
              ANCHOR DIAGNOSTICS BANNER — surfaces what the backend saw
              ============================================================ */}
          {perSheet.map((p) => {
            const d = p.diagnostics;
            if (!d) return null;
            // Cold start (no anchor set on this sheet)
            if (!d.anchorExists) {
              return (
                <div key={`diag-${p.sheetId}`}
                  className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm"
                  data-testid={`anchor-diag-${p.sheetId}`}>
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-amber-900">
                        Cold start — no anchor set on &quot;{p.sheetName}&quot;
                      </p>
                      <p className="text-xs text-amber-800 mt-1">
                        Returning every valid row in the sheet. Click <strong>&quot;Set Initial Anchor Now&quot;</strong> below to set your last imported lead as the anchor. After that, only new leads above the anchor will be returned.
                      </p>
                      <pre className="text-[10px] mt-2 text-amber-900/70 bg-amber-100/60 rounded p-2 overflow-auto">
{`sheetId               = ${d.sheetId}
sheetName             = ${d.sheetName}
anchorMobile (DB)     = ${d.anchorMobileRaw ?? '<null>'}
anchorMobile (norm)   = ${d.anchorMobileNormalized ?? '<null>'}
anchorExists?         = NO
mappingMobileColumn   = ${d.mappingMobileColumn ?? '<not mapped>'}
firstRow.rawMobile    = ${d.firstRowSample?.rawFromSheet ?? '<none>'}
firstRow.normalized   = ${d.firstRowSample?.normalized ?? '<none>'}
firstRow.name         = ${d.firstRowSample?.name ?? '<none>'}
scannedRows           = ${d.scannedRows} / ${d.totalRowsInSheet}
fix_version           = ${d.fix_version}`}
                      </pre>
                      <button
                        style={{marginTop:'12px',padding:'6px 14px',background:'#d97706',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'13px',display:'flex',alignItems:'center',gap:'6px'}}
                        onClick={() => {
                          const sheetObj = (sheets||[]).find(s=>s.id===p.sheetId);
                          if(sheetObj && openAnchorDialog) openAnchorDialog(sheetObj);
                        }}
                      >
                        &#9737; Set Initial Anchor Now
                      </button>
                    </div>
                  </div>
                </div>
              );
            }
            // Anchor set but not found in sheet
            if (d.anchorExists && !d.anchorFound) {
              return (
                <div key={`diag-${p.sheetId}`}
                  className="border border-rose-300 bg-rose-50 rounded-md p-3 text-sm"
                  data-testid={`anchor-diag-${p.sheetId}`}>
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-rose-900">
                        Anchor set but NOT FOUND in sheet — scan went all the way to the bottom
                      </p>
                      <p className="text-xs text-rose-800 mt-1">{d.message}</p>
                      <pre className="text-[10px] mt-2 text-rose-900/80 bg-rose-100/70 rounded p-2 overflow-auto">
{`sheetId               = ${d.sheetId}
sheetName             = ${d.sheetName}
anchorMobile (DB)     = ${d.anchorMobileRaw ?? '<null>'}
anchorMobile (norm)   = ${d.anchorMobileNormalized}
anchorName            = ${d.anchorName ?? '<none>'}
anchorFound           = NO
mappingMobileColumn   = ${d.mappingMobileColumn ?? '<not mapped>'}
firstRow.rawMobile    = ${d.firstRowSample?.rawFromSheet ?? '<none>'}
firstRow.normalized   = ${d.firstRowSample?.normalized ?? '<none>'}
firstRow.name         = ${d.firstRowSample?.name ?? '<none>'}
scannedRows           = ${d.scannedRows} / ${d.totalRowsInSheet}
fix_version           = ${d.fix_version}`}
                      </pre>
                    </div>
                  </div>
                </div>
              );
            }
            // Anchor matched — happy path
            return (
              <div key={`diag-${p.sheetId}`}
                className="border border-emerald-300 bg-emerald-50 rounded-md p-3 text-sm"
                data-testid={`anchor-diag-${p.sheetId}`}>
                <div className="flex items-start gap-2">
                  <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-emerald-900">
                      ✅ Anchor matched at row {d.stoppedAtRow} — only rows above are returned
                    </p>
                    <p className="text-xs text-emerald-800 mt-1">
                      Anchor: <span className="font-medium">{d.anchorName || '(no name)'}</span>
                      {' · '}<span className="font-mono">{d.anchorMobileNormalized}</span>
                      {' · '}scanned {d.scannedRows} of {d.totalRowsInSheet} rows ·
                      rows below anchor NOT scanned.
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Per-sheet stats grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {perSheet.map((p) => (
              <div key={p.sheetId} className={`border rounded-lg p-3 ${
                (p.newLeadsCount > 0) ? 'bg-emerald-50 border-emerald-200' :
                (p.status !== 'ok') ? 'bg-amber-50 border-amber-200' : 'bg-muted'
              }`} data-testid={`per-sheet-${p.sheetId}`}>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm">{p.sheetName}</p>
                  <p className="text-xl font-bold">{p.newLeadsCount}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {p.status === 'ok' ? (
                    <>scanned {p.scannedRows} of {p.totalRowsInSheet} rows
                    {p.stoppedAtRow ? ` · stopped at row ${p.stoppedAtRow}` : ''}</>
                  ) : (
                    <span className="text-amber-700">{p.error}</span>
                  )}
                </p>
              </div>
            ))}
          </div>

          {/* New leads table */}
          {newLeads.length > 0 && !dupResult && (
            <div className="border rounded-md overflow-hidden">
              <div className="overflow-x-auto max-h-[420px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-14">Row</TableHead>
                      <TableHead>Sheet</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {newLeads.map((lead) => (
                      <TableRow key={`${lead.sheetId}-${lead.rowIndex}-${lead.mobile}`}
                        data-testid={`new-lead-${lead.mobile}`}>
                        <TableCell className="font-mono text-xs">#{lead.rowIndex}</TableCell>
                        <TableCell><Badge variant="outline">{lead.sheetName}</Badge></TableCell>
                        <TableCell className="font-medium">{lead.name || '—'}</TableCell>
                        <TableCell className="font-mono text-sm">{lead.mobile}</TableCell>
                        <TableCell className="text-xs">{lead.email || '—'}</TableCell>
                        <TableCell>{lead.city || '—'}</TableCell>
                        <TableCell>{lead.state || '—'}</TableCell>
                        <TableCell className="text-xs">{lead.inquiryDate || '—'}</TableCell>
                        <TableCell><Badge variant="secondary">{lead.leadSource || '—'}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Duplicate result */}
      {dupResult && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Stat label="Total Checked" value={dupResult.totalChecked} testId="dup-total" />
            <Stat label="Unique" value={dupResult.uniqueCount} tone="success" testId="dup-unique" />
            <Stat label="Duplicates" value={dupResult.duplicateCount} tone="danger" testId="dup-dups" />
          </div>

          {/* Unique leads */}
          <Card data-testid="unique-card">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-emerald-700 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" /> Unique Leads ({dupResult.uniqueCount})
                  </CardTitle>
                  <CardDescription>Select the leads you want to import into LMS</CardDescription>
                </div>
                {dupResult.uniqueCount > 0 && !importResult && (
                  <Button onClick={handleImport} disabled={importing || selectedCount === 0}
                    size="lg" data-testid="import-btn">
                    {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              : <Upload className="h-4 w-4 mr-2" />}
                    Import {selectedCount} Selected
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {dupResult.uniqueCount === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>No unique leads</AlertTitle>
                  <AlertDescription>
                    All newly detected leads already exist in LMS, Servall, or R2G.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <div className="overflow-x-auto max-h-[420px]">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead className="w-10">
                            <Checkbox
                              checked={dupResult.uniqueLeads.length > 0 && dupResult.uniqueLeads.every((l) => selected[leadKey(l)])}
                              onCheckedChange={toggleAll}
                              data-testid="toggle-all"
                            />
                          </TableHead>
                          <TableHead>Sheet</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Mobile</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>City</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Source</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dupResult.uniqueLeads.map((l) => (
                          <TableRow key={leadKey(l)} className="bg-emerald-50/30 hover:bg-emerald-50/60"
                            data-testid={`uniq-${l.mobile}`}>
                            <TableCell>
                              <Checkbox
                                checked={!!selected[leadKey(l)]}
                                onCheckedChange={(v) => setSelected((s) => ({ ...s, [leadKey(l)]: !!v }))}
                                disabled={!!importResult}
                              />
                            </TableCell>
                            <TableCell><Badge variant="outline">{l.sheetName}</Badge></TableCell>
                            <TableCell className="font-medium">{l.name || '—'}</TableCell>
                            <TableCell className="font-mono text-sm">{l.mobile}</TableCell>
                            <TableCell className="text-xs">{l.email || '—'}</TableCell>
                            <TableCell>{l.city || '—'}</TableCell>
                            <TableCell className="text-xs">{l.inquiryDate || '—'}</TableCell>
                            <TableCell><Badge variant="secondary">{l.leadSource || '—'}</Badge></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Duplicate leads */}
          {dupResult.duplicateCount > 0 && (
            <Card data-testid="dup-card">
              <CardHeader>
                <CardTitle className="text-rose-700 flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5" /> Duplicate Leads ({dupResult.duplicateCount})
                </CardTitle>
                <CardDescription>
                  These mobiles already exist in LMS / Servall / R2G — they won&apos;t be imported.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-md overflow-hidden">
                  <div className="overflow-x-auto max-h-[420px]">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead>Sheet</TableHead>
                          <TableHead>Incoming</TableHead>
                          <TableHead>Mobile</TableHead>
                          <TableHead>Found In</TableHead>
                          <TableHead>Existing Name</TableHead>
                          <TableHead>Lead Source</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>City</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dupResult.duplicateLeads.map((l) => (
                          <TableRow key={`${leadKey(l)}-dup`} className="bg-rose-50/30 hover:bg-rose-50/60"
                            data-testid={`dup-${l.mobile}`}>
                            <TableCell><Badge variant="outline">{l.sheetName}</Badge></TableCell>
                            <TableCell className="font-medium">{l.name || '—'}</TableCell>
                            <TableCell className="font-mono text-sm">{l.mobile}</TableCell>
                            <TableCell>
                              <Badge variant={SOURCE_TONE[l.duplicate?.foundIn] || 'secondary'}>
                                {l.duplicate?.foundIn}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{l.duplicate?.existingName}</TableCell>
                            <TableCell><Badge variant="outline">{l.duplicate?.leadSource}</Badge></TableCell>
                            <TableCell>{l.duplicate?.status}</TableCell>
                            <TableCell>{l.duplicate?.city}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Import result */}
          {importResult && (
            <Card data-testid="import-result-card" className="border-emerald-300">
              <CardHeader>
                <CardTitle className="text-emerald-700 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" /> Import complete
                </CardTitle>
                <CardDescription>
                  These leads were added to LMS and tagged so the scanner stops at them next time.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Stat label="Leads Added to LMS" value={importResult.imported} tone="success" />
                  <Stat label="Errors" value={importResult.errorCount}
                    tone={importResult.errorCount ? 'danger' : 'default'} />
                </div>
                {importResult.errorCount > 0 && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>{importResult.errorCount} rows failed</AlertTitle>
                    <AlertDescription className="text-xs">
                      First error: {importResult.errorSamples?.[0]?.error}
                    </AlertDescription>
                  </Alert>
                )}
                <div className="mt-4">
                  <Button variant="outline" onClick={resetWorkflow} data-testid="start-over-btn">
                    Run another check
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================
// SUB-COMPONENT: SINGLE-SHEET DETAIL VIEW
// ============================================================
const SheetDetailView = ({
  sheet, checking, checkScope, handleCheckNewSingle, openMappingEditor,
  checkResult, dupResult, checkingDup, handleCheckDuplicates,
  selected, setSelected, selectedCount, toggleAll, leadKey,
  importing, handleImport, importResult, resetWorkflow, historyItems,
  handleToggle, openEditDialog, openAnchorDialog, handleClearAnchor,
}) => {
  return (
    <div className="space-y-6">
      {/* Sheet info */}
      <Card data-testid="sheet-detail-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2 flex-wrap">
                <Database className="h-5 w-5 text-emerald-600" />
                <span className="truncate">{sheet.name}</span>
                <StatusBadge status={sheet.status} />
                {sheet.mappingSaved
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Mapping Saved</Badge>
                  : <Badge variant="destructive">Mapping Required</Badge>}
              </CardTitle>
              <CardDescription className="break-all mt-1">
                <a href={sheet.sheetUrl} target="_blank" rel="noreferrer" className="underline text-sky-700 inline-flex items-center gap-1">
                  <Link2 className="h-3.5 w-3.5" /> {sheet.sheetUrl}
                </a>
              </CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => openMappingEditor(sheet)}
                data-testid="open-mapping-btn">
                <Settings2 className="h-4 w-4 mr-2" /> Edit Mapping
              </Button>
              <Button onClick={() => handleCheckNewSingle(sheet)} disabled={checking || !sheet.mappingSaved || sheet.status !== 'active'}
                data-testid="check-new-btn">
                {checking && checkScope?.sheetId === sheet.id
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <RefreshCw className="h-4 w-4 mr-2" />}
                Check New Leads
              </Button>
              <Button variant="outline" onClick={() => openEditDialog && openEditDialog(sheet)}
                data-testid="edit-sheet-btn" title="Edit name / URL">
                <Pencil className="h-4 w-4 mr-2" /> Edit
              </Button>
              <Button variant={sheet.status === 'active' ? 'outline' : 'default'}
                onClick={() => handleToggle && handleToggle(sheet)}
                data-testid="toggle-sheet-btn"
                title={sheet.status === 'active' ? 'Disable monitoring' : 'Enable monitoring'}>
                {sheet.status === 'active'
                  ? <><PowerOff className="h-4 w-4 mr-2" /> Disable</>
                  : <><Power className="h-4 w-4 mr-2" /> Enable</>}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Status" value={sheet.status === 'active' ? 'Active' : 'Disabled'}
                  tone={sheet.status === 'active' ? 'success' : 'default'} />
            <Stat label="Total Imported" value={sheet.totalImportedLeads ?? 0} tone="info" />
            <Stat label="Total Duplicate" value={sheet.totalDuplicateLeads ?? 0}
                  tone={(sheet.totalDuplicateLeads ?? 0) > 0 ? 'warn' : 'default'} />
            <Stat label="Last Checked" value={sheet.lastCheckedAt ? fmtDateTime(sheet.lastCheckedAt) : 'Never'} />
            <Stat label="Last Imported" value={sheet.lastImportedAt ? fmtDateTime(sheet.lastImportedAt) : 'Never'} />
          </div>
          {sheet.anchorMobile ? (
            <div className="mt-4 p-3 rounded-md border bg-muted/30 text-sm flex items-center gap-2 flex-wrap">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              <span className="text-muted-foreground">Anchor Lead:</span>
              <span className="font-medium">{sheet.anchorName || '—'}</span>
              <span className="text-muted-foreground">·</span>
              <span className="font-mono text-xs">{sheet.anchorMobile}</span>
              {sheet.anchorUpdatedAt && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-xs text-muted-foreground">set {fmtDateTime(sheet.anchorUpdatedAt)}</span>
                </>
              )}
              <Badge variant="outline" className="ml-1 text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                Auto-advances on every successful import
              </Badge>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="ghost"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => openAnchorDialog && openAnchorDialog(sheet)}
                  data-testid="update-anchor-btn"
                  title="Admin/Recovery only — not part of the normal daily workflow">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Override (Admin)
                </Button>
                <Button size="sm" variant="ghost"
                  className="text-xs text-rose-600 hover:text-rose-700"
                  onClick={() => handleClearAnchor && handleClearAnchor(sheet)}
                  data-testid="clear-anchor-btn"
                  title="Admin/Recovery only — clears the anchor so the next check returns all rows">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear (Admin)
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-4 p-3 rounded-md border border-dashed bg-amber-50/50 text-sm flex items-start gap-2 flex-wrap">
              <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5" />
              <div className="flex-1 min-w-[260px]">
                <p className="text-amber-900">
                  <strong>First-time setup:</strong> no anchor on this sheet yet.
                </p>
                <p className="text-xs text-amber-800/80 mt-1">
                  Set the initial Anchor <strong>once</strong> with your most-recent imported lead (e.g. the last
                  one already in LMS). After that, the anchor moves forward <strong>automatically</strong> on
                  every successful import — you will never have to touch it again.
                </p>
              </div>
              <Button size="sm" variant="outline" className="ml-auto"
                onClick={() => openAnchorDialog && openAnchorDialog(sheet)}
                data-testid="set-anchor-btn">
                <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Set Initial Anchor
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Check results */}
      {checkResult && checkScope?.sheetId === sheet.id && (
        <CheckResultsView
          checkResult={checkResult}
          checkScope={checkScope}
          dupResult={dupResult}
          checkingDup={checkingDup}
          handleCheckDuplicates={handleCheckDuplicates}
          selected={selected}
          setSelected={setSelected}
          selectedCount={selectedCount}
          toggleAll={toggleAll}
          leadKey={leadKey}
          importing={importing}
          handleImport={handleImport}
          importResult={importResult}
          resetWorkflow={resetWorkflow}
        />
      )}

      {/* History */}
      <Card data-testid="history-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HistoryIcon className="h-5 w-5" /> Monitoring History
          </CardTitle>
          <CardDescription>Every import for this sheet is recorded</CardDescription>
        </CardHeader>
        <CardContent>
          {historyItems.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No imports yet</p>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead className="text-right">Selected</TableHead>
                    <TableHead className="text-right">Imported</TableHead>
                    <TableHead className="text-right">Errors</TableHead>
                    <TableHead>By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyItems.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="text-xs">{fmtDateTime(h.checkedAt)}
                        {h.legacyMigrated && <Badge variant="outline" className="ml-2 text-xs">migrated</Badge>}
                      </TableCell>
                      <TableCell className="text-right font-mono">{h.selectedCount ?? '—'}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{h.importedCount ?? 0}</TableCell>
                      <TableCell className="text-right font-mono text-rose-700">{h.errorCount ?? 0}</TableCell>
                      <TableCell>{h.importedByName ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================
// SUB-COMPONENT: MAPPING EDITOR
// ============================================================
const MappingEditor = ({ sheet, loading, data, draft, setDraft, applySuggestion, saving, onSave, onCancel }) => {
  if (loading || !data) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
        <p className="text-sm text-muted-foreground mt-3">Reading columns from your Google Sheet…</p>
      </div>
    );
  }
  const NONE = '__none__';
  return (
    <div className="space-y-6">
      <Card data-testid="mapping-editor-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" /> Column Mapping — {sheet?.name || 'New Sheet'}
          </CardTitle>
          <CardDescription>
            Map each LMS field to one of your sheet&apos;s columns.
            Mobile is required. Other fields are optional.
            <br />
            <span className="text-xs">Sheet has <strong>{data.headers.length}</strong> columns
              · <strong>{data.totalRowsApprox}</strong> data rows.</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={applySuggestion} data-testid="apply-suggestion-btn">
              <Sparkles className="h-4 w-4 mr-2" /> Apply Smart Suggestion
            </Button>
            {data.mappingSaved && (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                Existing mapping loaded — changes will overwrite
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            {(data.lmsFields || []).map((f) => {
              const val = draft[f.key] || '';
              const suggested = data.suggestedMapping?.[f.key];
              const isSuggested = suggested && suggested === val;
              return (
                <div key={f.key} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">
                      {f.label}{f.required && <span className="text-rose-600 ml-0.5">*</span>}
                    </Label>
                    {isSuggested && (
                      <Badge variant="outline" className="text-xs">
                        <Sparkles className="h-3 w-3 mr-1" /> auto-suggested
                      </Badge>
                    )}
                  </div>
                  <Select
                    value={val || NONE}
                    onValueChange={(v) => setDraft({ ...draft, [f.key]: v === NONE ? '' : v })}
                  >
                    <SelectTrigger data-testid={`map-${f.key}`}>
                      <SelectValue placeholder="— Not Mapped —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— Not Mapped —</SelectItem>
                      {data.headers.map((h) => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>

          {/* Sample preview */}
          {data.sampleRows && data.sampleRows.length > 0 && (
            <div className="mt-6">
              <Label className="text-sm">Sample data (first 5 rows of your sheet)</Label>
              <div className="border rounded-md overflow-hidden mt-2">
                <div className="overflow-x-auto max-h-[260px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {data.headers.slice(0, 10).map((h) => (
                          <TableHead key={h} className="whitespace-nowrap text-xs">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.sampleRows.slice(0, 5).map((row, i) => (
                        <TableRow key={i}>
                          {data.headers.slice(0, 10).map((h) => (
                            <TableCell key={h} className="text-xs whitespace-nowrap">
                              {(row[h] || '').slice(0, 40)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={onSave} disabled={saving} data-testid="save-mapping-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          Save Mapping
        </Button>
      </div>
    </div>
  );
};

export default SheetMonitorPage;
